import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

export interface BranchMapOptions {
  element: HTMLElement;
  latitude: number;
  longitude: number;
  zoom?: number;
  title?: string;
}

export interface BranchMapInstance {
  map: google.maps.Map;
  marker: google.maps.marker.AdvancedMarkerElement;
}

declare global {
  interface Window {
    __onGoogleMapsReady?: () => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private loadPromise: Promise<typeof google.maps> | null = null;

  load(): Promise<typeof google.maps> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.loadGoogleMapsApi();
    return this.loadPromise;
  }

  async createBranchMap(options: BranchMapOptions): Promise<BranchMapInstance> {
    await this.load();

    const mapId = environment.googleMapsMapId;
    if (!mapId) {
      throw new Error('Google Maps Map ID is not configured (window.__env.googleMapsMapId)');
    }

    const { Map } = (await google.maps.importLibrary(
      'maps',
    )) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement } = (await google.maps.importLibrary(
      'marker',
    )) as google.maps.MarkerLibrary;

    const position = { lat: options.latitude, lng: options.longitude };

    const map = new Map(options.element, {
      center: position,
      zoom: options.zoom ?? 18,
      mapId,
    });

    const marker = new AdvancedMarkerElement({
      map,
      position,
      title: options.title,
    });

    return { map, marker };
  }

  private loadGoogleMapsApi(): Promise<typeof google.maps> {
    if (typeof google !== 'undefined' && google.maps) {
      return Promise.resolve(google.maps);
    }

    const apiKey = environment.googleMapsApiKey;
    if (!apiKey) {
      return Promise.reject(
        new Error(
          'Google Maps API key is not configured (window.__env.googleMapsApiKey)',
        ),
      );
    }

    return new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-google-maps-loader="true"]',
      );
      if (existing) {
        window.__onGoogleMapsReady = () => resolve(google.maps);
        return;
      }

      window.__onGoogleMapsReady = () => {
        resolve(google.maps);
        delete window.__onGoogleMapsReady;
      };

      const script = document.createElement('script');
      script.dataset['googleMapsLoader'] = 'true';
      script.async = true;
      script.src =
        'https://maps.googleapis.com/maps/api/js' +
        `?key=${encodeURIComponent(apiKey)}` +
        '&v=weekly&loading=async&callback=__onGoogleMapsReady';
      script.onerror = () => {
        this.loadPromise = null;
        delete window.__onGoogleMapsReady;
        reject(new Error('Failed to load Google Maps JavaScript API'));
      };
      document.head.appendChild(script);
    });
  }
}
